import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import KioskModal from './KioskModal';
import { endKioskSession } from '../../services/kioskServiceSession';
import { screenTimeouts } from '../../utils/apparatPolicy';

const InactivityModal = () => {
  const { t } = useTranslation();
  // Таймауты задаются в панели для каждого аппарата (настройки экрана)
  const priceApparat = useSelector((state) => state.payments.priceApparat);
  const { inactivityMs, countdownSec } = screenTimeouts(priceApparat);
  const [isInactive, setIsInactive] = useState(false);
  const [countdown, setCountdown] = useState(countdownSec);
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);

  // Сброс таймера бездействия при активности пользователя
  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      // Отсчёт — со свежего значения: настройки могли прийти после старта
      setCountdown(countdownSec);
      setIsInactive(true);
    }, inactivityMs);
  }, [inactivityMs, countdownSec]);

  // Подтверждение активности пользователем
  const handleConfirm = () => {
    setIsInactive(false);
    setCountdown(countdownSec);
    resetInactivityTimer();
  };

  useEffect(() => {
    resetInactivityTimer();

    const handleActivity = () => resetInactivityTimer();
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    return () => {
      clearTimeout(inactivityTimerRef.current);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, [resetInactivityTimer]);

  // Обратный отсчет в модальном окне
  useEffect(() => {
    if (!isInactive) return;

    const countdownTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          endKioskSession();
          navigate('/');
          setIsInactive(false);
          return countdownSec;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [isInactive, navigate, countdownSec]);

  return (
    <AnimatePresence>
      {isInactive && (
        <KioskModal
          tone="accent"
          // системные модалки должны перекрывать модалки экранов (те на z-50)
          zClass="z-[60]"
          title={t('inactivityModal.title')}
          description={t('inactivityModal.redirect', { count: countdown })}
          descriptionKey={countdown}
          icon={
            <svg
              className="w-[5.4vh] h-[5.4vh]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" />
            </svg>
          }
          footer={
            <button data-analytics="inactivitymodal.inactivityModal.confirm.1"
              type="button"
              onClick={handleConfirm}
              className="w-full py-[2.2vh] rounded-[3vh] bg-[#246CE2] text-[2.2vh] font-bold text-white active:bg-[#1B55B4] transition-colors"
            >
              {t('inactivityModal.confirm')}
            </button>
          }
        />
      )}
    </AnimatePresence>
  );
};

export default InactivityModal;
